import React, { useCallback, useState } from "react";
import BaseModal from "../../../../components/ui/BaseModal";
import BaseTextField from "../../../../components/ui/BaseTextField";
import BaseButton from "../../../../components/ui/BaseButton";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useMutation } from "@tanstack/react-query";
import { useDispatch } from "react-redux";
import { adminUpdateUserProfile } from "../../../../services/api/user";
import { showToast } from "../../../../store/toast/toastSlice";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/material.css";
import { FormHelperText } from "@mui/material";
import BaseAutoSelect from "../../../../components/ui/BaseAutoSelect";
import SearchLocation from "../../../../components/SearchLocation/SearchLocation";
import { deactivateCompanyUser, updateCompanyUser } from "../../../../services/api/company";
import ConfirmDeactivateUserModal from "../../../CompanySettings/CompanyUsers/components/ConfirmDeactivateCompnayUserModal";
import useAuth from "../../../../hooks/useAuth";
import { normalizePhoneNumber } from "../../../../utils/helpers";

const validationSchema = Yup.object({
  firstName: Yup.string()
    .trim()
    .matches(/^[A-Za-z\s]*$/, "First name must only contain letters")
    .max(100, "Max 100 characters"),
  lastName: Yup.string()
    .trim()
    .matches(/^[A-Za-z\s]*$/, "Last name must only contain letters")
    .max(100, "Max 100 characters"),
  email: Yup.string().matches(
    /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
    "Invalid email format",
  ),
  phoneNumber: Yup.string().trim(),
});

const AddEditPersonalModal = ({
  open,
  close,
  userId,
  initialValues = {
    firstName: "",
    lastName: "",
    email: "",
    phoneNumber: "",
    role: "",
    status: "",
    phoneNumber: "",
    personalAddress: {},
  },
  refetchMe,
}) => {
  const dispatch = useDispatch();
  const [isConfrimDeactivateUserOpen, setIsConfrimDeactivateUserOpen] = useState(false);
  const { authRole } = useAuth();

  const { mutateAsync: adminUpdateUserProfileMutation, isPending } = useMutation({
    mutationFn: adminUpdateUserProfile,
  });
  const {
    isPending: isUpdateCompanyUserApiLoading,
    error: isUpdateCompanyUserApiError,
    data: updatedUser,
    mutateAsync: updateCompanyUserMutation,
  } = useMutation({
    mutationFn: updateCompanyUser,
    enabled: false,
  });
  const { isPending: isDeactivating, mutateAsync: deactivateUserMutation } = useMutation({
    mutationFn: deactivateCompanyUser,
  });

  const buildPayload = (values) =>
    Object.fromEntries(
      Object.entries(values)
        .map(([k, v]) => [k, typeof v === "string" ? v.trim() : v])
        .filter(([, v]) => v !== "" && v !== undefined),
    );

  const getErrorToastMessage = (err) =>
    err?.response?.data?.message ||
    (err?.response?.status === 409
      ? "Email already in use."
      : err?.response?.status === 403
        ? "Insufficient permissions."
        : "Failed to update profile.");

  const handleSubmit = useCallback(
    async (values) => {
      try {
        const data = buildPayload(values);

        // 🚀 ONLY open confirm modal
        if (authRole === "CLIENT") {
          if (data.status === "DEACTIVATED") {
            setIsConfrimDeactivateUserOpen(true);
            return;
          }

          const payload = {
            id: userId,
            ...data,
          };

          await updateCompanyUserMutation(payload);
        } else {
          await adminUpdateUserProfileMutation({
            userId,
            data: buildPayload(values),
          });
        }
        dispatch(
          showToast({
            message: "Profile updated successfully",
            type: "success",
          }),
        );

        await refetchMe?.();
        close(false);
      } catch (err) {
        dispatch(
          showToast({
            message: getErrorToastMessage(err),
            type: "error",
          }),
        );
      }
    },
    [userId, updateCompanyUserMutation, dispatch, refetchMe, close],
  );
  const formikInitialValues = {
    ...initialValues,
    addressLine1: initialValues.personalAddress?.addressLine1 || "",
    country: initialValues.personalAddress?.country || "",
    city: initialValues.personalAddress?.city || "",
    state: initialValues.personalAddress?.state || "",
    zipCode: initialValues.personalAddress?.zipCode || "",
    latitude: initialValues.personalAddress?.latitude || "",
    longitude: initialValues.personalAddress?.longitude || "",
  };
  const formik = useFormik({
    initialValues: formikInitialValues,
    validationSchema,
    enableReinitialize: true,
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: handleSubmit,
  });
  const handlePlaceSelected = (selectedAddress) => {
    formik.setValues({
      ...formik.values,
      country: selectedAddress.country,
      addressLine1: selectedAddress.completeAddress,
      city: selectedAddress.city,
      state: selectedAddress.state,
      zipCode: selectedAddress.zipCode,
      longitude: selectedAddress.longitude,
      latitude: selectedAddress.latitude,
      timeZone: selectedAddress.timeZone,
    });
  };
  const handleStatusChange = (value) => {
    if (value === "DEACTIVATED") {
      setIsConfrimDeactivateUserOpen(true);
    } else {
      formik.setFieldValue("status", value);
    }
  };
  return (
    <BaseModal className="-top-[200px]" open={open} close={close} style={{ width: "1060px" }}>
      <form onSubmit={formik.handleSubmit}>
        <div className="py-4 px-8">
          <div className="flex justify-between items-center border-b-2 pb-1">
            <h5 className="text-dark font-normal">Edit Profile</h5>
          </div>
        </div>

        <div className="px-8 pb-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3 md:gap-8">
            {authRole !== "CLIENT" && (
              <>
                <BaseTextField
                  id="firstName"
                  name="firstName"
                  label="First Name"
                  labelShrink={false}
                  value={formik.values.firstName}
                  onChange={(e) => /^[A-Za-z\s]*$/.test(e.target.value) && formik.handleChange(e)}
                  onBlur={formik.handleBlur}
                  error={Boolean(formik.touched.firstName && formik.errors.firstName)}
                  helperText={formik.touched.firstName && formik.errors.firstName}
                  fullWidth
                />
                <BaseTextField
                  id="lastName"
                  name="lastName"
                  label="Last Name"
                  labelShrink={false}
                  value={formik.values.lastName}
                  onChange={(e) => /^[A-Za-z\s]*$/.test(e.target.value) && formik.handleChange(e)}
                  onBlur={formik.handleBlur}
                  error={Boolean(formik.touched.lastName && formik.errors.lastName)}
                  helperText={formik.touched.lastName && formik.errors.lastName}
                  fullWidth
                />
              </>
            )}
            <BaseTextField
              id="email"
              name="email"
              label={`${authRole !== "CLIENT" ? "Email" : "User"}`}
              labelShrink={false}
              value={formik.values.email}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={Boolean(formik.touched.email && formik.errors.email)}
              helperText={formik.touched.email && formik.errors.email}
              fullWidth
              optional={authRole === "FREELANCER"}
              disabled={authRole === "CLIENT"}
            />
            {authRole === "CLIENT" && (
              <>
                <BaseTextField
                  id="role"
                  name="role"
                  label="Role"
                  labelShrink={false}
                  value={formik.values.role}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={Boolean(formik.touched.role && formik.errors.role)}
                  helperText={formik.touched.role && formik.errors.role}
                  fullWidth
                  optional={initialValues?.role === "FREELANCER"}
                  disabled={initialValues?.role !== "FREELANCER"}
                />
                <SearchLocation onPlaceSelected={handlePlaceSelected} isDefaultField={true} />
                <BaseTextField
                  fullHeight={false}
                  fullWidth
                  labelShrink={false}
                  id="address"
                  name="address"
                  label="Address"
                  value={formik.values?.addressLine1}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  className="col-span-4"
                  optional={false}
                  isDefaultField={false}
                  error={formik.touched.addressLine1 && formik.errors.addressLine1}
                  helperText={formik.touched.addressLine1 && formik.errors.addressLine1}
                />
                <BaseTextField
                  fullHeight={false}
                  fullWidth
                  labelShrink={false}
                  id="country"
                  name="country"
                  label="Country"
                  value={formik.values?.country || ""}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  className="col-span-4"
                  optional={false}
                  isDefaultField={false}
                  error={formik.touched.country && formik.errors.country}
                  helperText={formik.touched.country && formik.errors.country}
                />
              </>
            )}
            <div>
              <label htmlFor="phoneNumber" className="block text-sm font-sm mb-1 text-[#505050]">
                Phone
              </label>
              <PhoneInput
                specialLabel=""
                country={"au"}
                value={formik.values.phoneNumber}
                // PhoneInput shows a "+" but reports bare digits, so the number was
                // persisted without its dialling code. `country` is the flag shown
                // in the field, which is what restores it.
                onChange={(val, country) =>
                  formik.setFieldValue("phoneNumber", normalizePhoneNumber(val, country))
                }
                onBlur={() => formik.setFieldTouched("phoneNumber", true)}
                dropdownStyle={{
                  maxHeight: "150px",
                }}
                inputStyle={{
                  height: "40px",
                  width: "100%",
                  borderRadius: "8px",
                  ...(formik.touched.phoneNumber &&
                    formik.errors.phoneNumber && {
                      border: "1px solid #d32f2f",
                    }),
                }}
                containerClass="w-full"
              />
              {formik.touched.phoneNumber && formik.errors.phoneNumber && (
                <FormHelperText error>{formik.errors.phoneNumber}</FormHelperText>
              )}
            </div>
            {authRole === "CLIENT" && (
              <BaseAutoSelect
                required={true}
                labelShrink={false}
                label="Status"
                name="status"
                disableCloseOnSelect
                placeholder="Select Status"
                options={[
                  { label: "Active", value: "APPROVED" },
                  { label: "Inactive", value: "DEACTIVATED" },
                ]}
                value={formik.values?.status}
                onChange={(e) => handleStatusChange(e.target.value)}
                onBlur={formik.handleBlur}
                isClearable
              />
            )}
          </div>
        </div>

        <div
          className="d-flex content-center mt-10 bg-white py-6 "
          style={{
            boxShadow: "0px 0px 14px 0px rgba(0, 0, 0, 0.07)",
          }}
        >
          {" "}
          <BaseButton
            type="button"
            variant="outlined"
            color="primary"
            className=" px-[28px] py-[5px] bg-light text-primary hover:text-primary"
            style={{
              marginRight: "16px",
            }}
            onClick={() => {
              formik.resetForm();
              close(false);
            }}
            disabled={isPending}
          >
            Cancel
          </BaseButton>
          <BaseButton
            loaderSize="22px"
            isLoading={isPending}
            color="primary"
            className="py-[5px] px-[38px]"
            type="submit"
          >
            Save
          </BaseButton>
        </div>
      </form>
      {isConfrimDeactivateUserOpen && (
        <ConfirmDeactivateUserModal
          deactivateModalOpen={isConfrimDeactivateUserOpen}
          setDeactivateModalOpen={setIsConfrimDeactivateUserOpen}
          userId={userId}
          onSuccess={() => {
            close(false); // ✅ close parent modal
          }}
          // refetchUsers={refetch}
        />
      )}
    </BaseModal>
  );
};

export default AddEditPersonalModal;
